// eslint-disable-next-line no-unused-vars
import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  setApplePaymentData,
  clearPaymentMethodsData,
  changePaymentMethod,
  paymentMethods,
} from '../../slices/payments';
import applePayService from '../../services/applePay';
import { getLastURIComponent, formatPhone } from '../../utils';
import states from '../../components/forms/AddressForm/states.json';
import countries from '../../components/forms/AddressForm/countries.json';
import messages from './messages';
import {
  setApplePayShippingAddressDraft,
  setNeedsToCheckApplePayShippingAddressDraft,
} from '../../slices/shippingAddress';
import { EMAIL_REGULAR_EXPRESSION } from '../../utils/common/constants';

import {
  COUNTRY_CODE,
  TOTAL_LABEL,
  TOTAL_TYPE,
  SUPPORTED_NETWORKS,
  MERCHANT_CAPABILITIES,
  REQUIRED_SHIPPING_FIELDS,
  APPLE_PAY_SESSION_VERSION,
  INVALID_SHIPPING_CONTACT_ERROR_CODE,
  APPLE_PAY_SHIPPING_FIELDS_MAP,
} from './constants';
import storage from '../../utils/storage';
import sessionStorageService from '../../services/sessionStorage';

const appleBuyerToAddress = ({ shippingContact }) => ({
  streetAddress: shippingContact.addressLines[0],
  streetName: shippingContact.addressLines[0],
  city: shippingContact.locality,
  state: shippingContact.administrativeArea?.toUpperCase(),
  postalCode: shippingContact.postalCode,
  country: shippingContact.countryCode?.toUpperCase(),
  firstName: shippingContact.givenName,
  lastName: shippingContact.familyName,
  email: shippingContact.emailAddress,
  phone: formatPhone(shippingContact.phoneNumber),
  additionalStreetInfo: shippingContact.addressLines[1],
});

const shippingAddressSchema = {
  email: (value) => EMAIL_REGULAR_EXPRESSION.test(value),
  phone: (value) => !!value,
  firstName: (value) => !!value,
  lastName: (value) => !!value,
  streetName: (value) => !!value,
  postalCode: (value) => !!value,
  city: (value) => !!value,
  country: (value) => countries.some(({ value: country }) => country === value),
  state: (value) => states.some(({ value: stateAbbr }) => stateAbbr === value),
};

const getApplePayErrorMessage = (fieldName) => {
  switch (fieldName) {
    case 'email':
      return messages.invalidEmailErrorMessage;
    case 'state':
      return messages.invalidStateErrorMessage;
    default:
      return messages.defaultErrorMessage;
  }
};

const listShippingAddressSchemaErrors = (address, formatMessage) =>
  Object.keys(shippingAddressSchema)
    .filter((field) => !shippingAddressSchema[field](address[field]))
    .map(
      (field) =>
        new window.ApplePayError(
          INVALID_SHIPPING_CONTACT_ERROR_CODE,
          APPLE_PAY_SHIPPING_FIELDS_MAP[field],
          formatMessage(getApplePayErrorMessage(field), { field }),
        ),
    );

const useApplePaySession = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const {
    amount, currencyCode, shippingAddress, cartId, isDraft,
  } =
    useSelector((state) => {
      const finalPrice =
        state.cart.taxedPrice?.totalGross || state.cart.totalPrice;
      return {
        amount: finalPrice.centAmount / 10 ** finalPrice.fractionDigits,
        centAmount: finalPrice.centAmount,
        currencyCode: finalPrice.currencyCode,
        shippingAddress: state.cart.shippingAddress,
        cartId: state.cart?.id,
        isDraft: state.cart?.isDraft,
      };
    });

  const completeOrderCallback = useCallback(
    ({ completeOrderFunction, isExpressCheckout = false }) => {
      const applePayAvailable = window.ApplePaySession?.canMakePayments?.();
      if (!applePayAvailable) return;

      dispatch(clearPaymentMethodsData());

      const paymentRequest = {
        countryCode: COUNTRY_CODE,
        currencyCode,
        total: {
          label: TOTAL_LABEL,
          type: TOTAL_TYPE,
          amount,
        },
        supportedNetworks: SUPPORTED_NETWORKS,
        merchantCapabilities: MERCHANT_CAPABILITIES,
        requiredShippingContactFields: REQUIRED_SHIPPING_FIELDS,
      };

      const session = new window.ApplePaySession(
        APPLE_PAY_SESSION_VERSION,
        paymentRequest,
      );

      session.oncancel = () => {};

      session.onvalidatemerchant = async () => {
        const response = await applePayService.getApplePaySession(
          cartId,
          isDraft,
        );
        session.completeMerchantValidation(response);
      };

      session.onpaymentauthorized = async (event) => {
        const { payment } = event;
        // applePaymentData will be encoded in base64
        const applePaymentData = btoa(
          JSON.stringify(payment.token.paymentData),
        );
        const { shippingContact } = payment;
        let formattedShippingContact;
        if (shippingContact) {
          formattedShippingContact = appleBuyerToAddress({ shippingContact });
          const shippingAddressErrorList = listShippingAddressSchemaErrors(
            formattedShippingContact,
            formatMessage,
          );

          if (shippingAddressErrorList?.length) {
            const paymentResult = {
              status: window.ApplePaySession.STATUS_FAILURE,
              errors: shippingAddressErrorList,
            };
            session.completePayment(paymentResult);
          }
        }

        session.completePayment({
          status: window.ApplePaySession.STATUS_SUCCESS,
        });
        const lastURIComponent = getLastURIComponent();
        // Start regular checkout block
        if (completeOrderFunction) {
          dispatch(setApplePaymentData(applePaymentData));
          completeOrderFunction({
            billingAddress: shippingAddress,
          });
        }

        dispatch(changePaymentMethod(paymentMethods.APPLE_PAY));

        if (
          formattedShippingContact &&
          (isExpressCheckout || !shippingAddress)
        ) {
          if (lastURIComponent.toLocaleLowerCase() === 'cart') {
            sessionStorageService.setItem({
              itemName: 'applePayShippingAddressDraft',
              value: JSON.stringify(formattedShippingContact),
            });
          } else if (lastURIComponent.toLocaleLowerCase() === 'checkout') {
            dispatch(setApplePayShippingAddressDraft(formattedShippingContact));
            dispatch(setNeedsToCheckApplePayShippingAddressDraft(true));
          }
        }
        dispatch(setApplePaymentData(applePaymentData));

        if (lastURIComponent.toLocaleLowerCase() === 'cart') {
          storage.cleanAllPaymentData();
          localStorage.setItem('applePaymentData', applePaymentData);
          window.location.href = '/checkout';
        }
      };

      session.begin();
    },
    [
      amount,
      cartId,
      currencyCode,
      dispatch,
      formatMessage,
      isDraft,
      shippingAddress,
    ],
  );
  
  const isApplePayAvailable = useMemo(
    () =>
      window.ApplePaySession?.canMakePayments?.() &&
      window?.purTogglesEnabled('apple_pay_payment'),
    [],
  );

  return { completeOrderCallback, isApplePayAvailable };
};

export default useApplePaySession;
